/* eslint-disable @typescript-eslint/no-empty-function */
import { Dialog } from "@finanedi.es/storybook"
import { useQuery } from "@tanstack/react-query"
import { getAlbaranByIdSimple } from "api/Albaran.service"
import { AlbaranReadingSimpleDtoProps } from "api/DTOs/AlbaranReadingSimpleDto"
import { DocumentoComercialAsociadoReadingDtoProps } from "api/DTOs/DocumentoComercialAsociadoReadingDto"
import { FacturaReadingSimpleDtoProps } from "api/DTOs/FacturaReadingSimpleDto"
import { FicheroCreationDtoProps } from "api/DTOs/FicheroCreationDto"
import { FicheroReadingDtoProps } from "api/DTOs/FicheroReadingDto"
import { FlujoTesoreriaCreationDtoProps } from "api/DTOs/FlujoTesoreriaCreationDto"
import { PedidoReadingSimpleDtoProps } from "api/DTOs/PedidoReadingSimpleDto"
import { PresupuestoReadingSimpleDtoProps } from "api/DTOs/PresupuestoReadingSimpleDto"
import { useEnumCodigoFacturaRectificativa } from "api/Enum.hook"
import {
  getFacturasByIdSimple,
  getFlujoTesoreriaFactura,
} from "api/Factura.service"
import { getPedidoByIdSimple } from "api/Pedido.service"
import { getPresupuestoByIdSimple } from "api/Presupuesto.service"
import { ESTADO_REVISION_DOCUMENTO_COMERCIAL } from "api/Types/EstadoRevisionDocumentoComercial"
import {
  TIPO_DOCUMENTO_ENUM,
  TipoDocumentoEnumType,
} from "api/Types/TipoDocumentoEnum"
import {
  TIPO_FLUJO_TESORERIA,
  TipoFlujoTesoreriaType,
} from "api/Types/TipoFlujoTesoreria"
import Modal from "common/components/Modal/Modal"
import NativePDF from "common/components/NativePDF/NativePDF"
import FicheroService from "common/services/FicheroService"
import {
  GET_PDF_DOCUMENTO_FN,
  TIPO_DOCUMENTO,
  TIPO_DOCUMENTO_DB,
} from "common/utils/documentos"
import { removeMimeFromBase64, toBase64 } from "common/utils/formats"
import getFileTypeToTheBE from "common/utils/getFileTypeToTheBE"
import notification from "common/utils/notification"
import {
  dataBasicFormat,
  FORMAT_STRING_DOCUMENT,
} from "features/Contabilidad/pages/DetalleImpuestos/components/Modelo303/constant"
import PagoCobroManualForm from "features/Documentos/components/ModalPagoCobroManual/ModalPagoCobroManual"
import NewPagoForm from "features/Tesoreria/components/NewPagoForm"
import useGetFactura from "features/Tesoreria/hooks/useGetFactura"
import { PAYMENT_TYPE_PAGOS_COBROS_ACTION_SETTINGS } from "features/Tesoreria/pages/PagosCobros/constants"
import DocumentDetailCard from "features/Validaciones/pages/Documentos/DocumentosDetail/components/DocumentDetailCard"
import fileDownload from "js-file-download"
import { split } from "lodash"
import { Skeleton } from "primereact/skeleton"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { ModalAsiento } from "./ModalAsiento"
import ModalEstados from "./ModalEstados"
import "./ModalVerDocumento.scss"
import ModalVerDocumentoArchivosAdjuntos from "./ModalVerDocumentoArchivosAdjuntos"
import ModalVerDocumentoDatosBasicos from "./ModalVerDocumentoDatosBasicos"
import ModalVerDocumentoHeader from "./ModalVerDocumentoHeader"
import ModalVerDocumentosDocumetosAsosiados from "./ModalVerDocumentosDocumetosAsosiados"
import ModalVerPagosYCobros from "./ModalVerPagosYCobros"
import AddPagoCobroModal from "./components/AddPagoCobroModal"
import {
  agregarArchivoAdjuntoDocumentoFetch,
  archivosAduntosDocumentoFetch,
  documentoPendienteValidarFetch,
  documentosAsociadosDocumentoFetch,
  downloadDocumentoFetch,
  eliminarArchivoDocumentoFetch,
  STATUSCORRECT,
  TAB_OPTIONS,
} from "./constants"

interface ModalVerDocumentoProps {
  showModal: boolean
  onHide: () => void
  documentoID: string | null
  docType: TipoDocumentoEnumType
  typeDocumentTable: keyof typeof FORMAT_STRING_DOCUMENT
  refreshData?: () => void
  showActions?: boolean
  isVenta?: boolean
}

const ModalVerDocumento = ({
  showModal,
  onHide,
  documentoID,
  docType,
  typeDocumentTable,
  refreshData = () => {},
  showActions = true,
  isVenta = true,
}: ModalVerDocumentoProps) => {
  const { t } = useTranslation()
  const { codigoFacturaRectificativa } = useEnumCodigoFacturaRectificativa()
  const [showAddPagoModal, setShowAddPagoModal] = useState({
    data: null,
    show: false,
  })
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false)
  const [showModalPagoCobroManual, setShowModalPagoCobroManual] = useState<{
    show: boolean
    id: string | undefined
    serie: string | null | undefined
  }>({ show: false, id: undefined, serie: undefined })
  const [tabSelected, setTabSelected] = useState(1)
  const { handleOpenModalFacturaDetail } = useGetFactura()
  const [newPagoCobro, setNewPagoCobro] = useState<string[]>([])

  const { data: documentoPendienteValidar } = useQuery({
    queryKey: ["documentoPendienteValidar", documentoID, docType],
    enabled: Boolean(documentoID),
    queryFn: () => {
      if (!documentoID) return
      return documentoPendienteValidarFetch(docType, documentoID)
    },
    refetchOnWindowFocus: false,
    retry: false,
    retryOnMount: false,
    refetchOnMount: "always",
  })

  const { data: documentSimple, refetch: refetchDocumentSimple } = useQuery<
    | { data: PresupuestoReadingSimpleDtoProps; tipo: "presupuesto" }
    | { data: AlbaranReadingSimpleDtoProps; tipo: "albaran" }
    | { data: PedidoReadingSimpleDtoProps; tipo: "pedido" }
    | { data: FacturaReadingSimpleDtoProps; tipo: "factura" }
    | undefined
  >({
    queryKey: ["documentoSimple", documentoID, docType],
    enabled: Boolean(documentoID),
    queryFn: async () => {
      if (!documentoID) return
      switch (docType) {
        case TIPO_DOCUMENTO_ENUM.PRESUPUESTO:
          return {
            data: await getPresupuestoByIdSimple(documentoID),
            tipo: "presupuesto",
          }
        case TIPO_DOCUMENTO_ENUM.ALBARAN:
          return {
            data: await getAlbaranByIdSimple(documentoID),
            tipo: "albaran",
          }
        case TIPO_DOCUMENTO_ENUM.PEDIDO:
          return {
            data: await getPedidoByIdSimple(documentoID),
            tipo: "pedido",
          }
        case TIPO_DOCUMENTO_ENUM.FACTURA:
          return {
            data: await getFacturasByIdSimple(documentoID),
            tipo: "factura",
          }
        default:
          throw new Error("Tipo de documento no soportado")
      }
    },
    refetchOnWindowFocus: false,
    retry: false,
    retryOnMount: false,
    refetchOnMount: "always",
  })

  const facturaVentaBorrador =
    documentSimple?.data?.estadoRevisionActual ===
    ESTADO_REVISION_DOCUMENTO_COMERCIAL.BORRADOR

  const { data: pdfDocument, isLoading: pdfDocumentIsLoading } = useQuery({
    queryKey: ["pdf", documentoID, docType],
    enabled: Boolean(documentoID),
    queryFn: () => {
      if (!documentoID) return
      return downloadDocumentoFetch(docType, documentoID)
    },
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      return (
        failureCount < 10 && error.message !== "Tipo de documento no soportado"
      )
    },
    retryDelay: 2000,
    retryOnMount: false,
    refetchOnMount: "always",
  })

  const {
    data: flujosTesoreriaFactura,
    isLoading: flujoTesoreriaFacturaIsLoading,
    refetch: flujoTesoreriaFacturaRefetch,
  } = useQuery({
    queryKey: ["flujoTesoreriaFactura", documentoID, docType],
    enabled: Boolean(documentoID && docType === TIPO_DOCUMENTO_ENUM.FACTURA),
    queryFn: () => {
      if (!documentoID) return
      return getFlujoTesoreriaFactura(documentoID)
    },
    refetchOnWindowFocus: false,
    retry: false,
    retryOnMount: false,
    refetchOnMount: "always",
  })

  const { data: archivosAdjuntos, refetch: archivosAdjuntosRefetch } = useQuery(
    {
      queryKey: ["archivosAdjuntos", documentoID, docType],
      enabled: Boolean(documentoID),
      queryFn: () => {
        if (!documentoID) return
        return archivosAduntosDocumentoFetch(docType, documentoID)
      },
      refetchOnWindowFocus: false,
      retry: false,
      retryOnMount: false,
      refetchOnMount: "always",
    }
  )

  const { data: documentosAsociados } = useQuery({
    queryKey: ["documentosAsociados", documentoID, docType],
    enabled: Boolean(documentoID),
    queryFn: () => {
      if (!documentoID) return
      return documentosAsociadosDocumentoFetch(docType, documentoID)
    },
    refetchOnWindowFocus: false,
    retry: false,
    retryOnMount: false,
    refetchOnMount: "always",
  })

  const eliminarArchivoAdjunto = async (fileID: string) => {
    if (!documentoID) return
    const response = await eliminarArchivoDocumentoFetch(
      fileID,
      docType,
      documentoID
    )
    if (response.status === STATUSCORRECT) {
      archivosAdjuntosRefetch()
      refreshData()
    }
  }

  const agregarArchivoAdjunto = async (archivos: File[]) => {
    if (!documentoID) return
    const archivo = archivos[0]
    const addArchivoData: FicheroCreationDtoProps = {
      nombre: split(archivo.name, ".", 1)[0],
      tipo: getFileTypeToTheBE(archivo.name),
      // @ts-expect-error TS(2345): Argument of type 'unknown' is not assignable to pa...
      fileAsBase64: removeMimeFromBase64(await toBase64(archivo)),
    }

    const response = await agregarArchivoAdjuntoDocumentoFetch(
      addArchivoData,
      docType,
      documentoID
    )
    if (response.status === STATUSCORRECT) {
      archivosAdjuntosRefetch()
      refreshData()
    }
  }

  const descargarArchivoAdjunto = async (file: FicheroReadingDtoProps) => {
    try {
      const { id, nombre, extension } = file
      const { data } = await FicheroService.descargarFichero(id)

      fileDownload(new Blob([data]), `${nombre}${extension}`)
    } catch (error) {
      notification.errorServidor({
        body: `${t("crear.presupuesto.modal.msg.downloadError")}`,
      })
    }
  }

  const descargarDocumentoAsociado = async (
    documento: DocumentoComercialAsociadoReadingDtoProps & { nombre: string }
  ) => {
    try {
      const { id, nombre, tipoDocumento } = documento
      // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre...
      const nombreTipoDeDocumento = TIPO_DOCUMENTO_DB[tipoDocumento]
      // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre...
      const tipoDocumentoID = TIPO_DOCUMENTO[nombreTipoDeDocumento]

      const { data } = await GET_PDF_DOCUMENTO_FN[tipoDocumentoID](id)

      fileDownload(new Blob([data]), `${nombre}.pdf`)
    } catch (error) {
      notification.errorServidor({
        body: `${t("crear.presupuesto.modal.msg.downloadError")}`,
      })
    }
  }

  const handleOpenCreateModal = (openModal: boolean) => {
    documentSimple?.tipo === "factura" &&
      documentSimple?.data?.importePendiente &&
      setShowCreateModal(openModal)
  }

  const handleCreate = async (
    key: TipoFlujoTesoreriaType,
    values: Partial<FlujoTesoreriaCreationDtoProps>
  ) => {
    const response = await PAYMENT_TYPE_PAGOS_COBROS_ACTION_SETTINGS()?.[
      key
    ].postService(values)

    if (response) {
      notification.success({
        body: `${
          PAYMENT_TYPE_PAGOS_COBROS_ACTION_SETTINGS()[key].title
        } creado con exito.`,
      })
      setNewPagoCobro([...newPagoCobro, response.data])
      handleOpenModalFacturaDetail({ id: documentoID })
      handleOpenCreateModal(false)
    }
  }

  const handleOpenCreateNewPago = () => {
    handleOpenCreateModal(true)
  }

  return (
    <Dialog
      header={
        <ModalVerDocumentoHeader
          docType={docType}
          document={documentSimple?.data}
          documentoID={documentoID}
          onHide={() => {
            onHide()
            setTabSelected(1)
          }}
          showActions={showActions}
          isVenta={isVenta}
          setShowModalPagoCobroManual={setShowModalPagoCobroManual}
          puedeBorrarse={Boolean(
            documentSimple?.tipo === "factura" &&
              documentSimple?.data?.puedeBorrarse
          )}
          refreshData={refreshData}
        />
      }
      headerClassName='[&>*:nth-child(2)]:!tw-pt-3'
      className='modal-ver-documento tw-min-w-320 tw-min-h-156.25 3xl:tw-min-h-225 tw-max-w-full tw-custom-resizableIcon'
      resizable
      maximizable
      maskClassName='!tw-z-20'
      visible={showModal}
      onHide={() => {
        onHide()
        setTabSelected(1)
      }}
      draggable
      closable
      closeOnEscape
      dismissableMask
      keepInViewport={false}
    >
      <div className='row modal-ver-documento__contenido'>
        <div className='col-5'>
          {documentSimple?.data && (
            <ModalVerDocumentoDatosBasicos
              datosBasicosDocumento={dataBasicFormat(
                documentSimple?.data,
                typeDocumentTable,
                t,
                codigoFacturaRectificativa
              )}
              estadoRevisionActual={documentSimple.data.estadoRevisionActual}
              document={documentSimple?.data}
              className='mb-2'
            />
          )}
          {!facturaVentaBorrador && (
            <>
              {documentSimple?.tipo === "factura" &&
                documentSimple?.data?.asientoContable && (
                  <ModalAsiento
                    asientoContable={documentSimple?.data?.asientoContable}
                    refreshData={refetchDocumentSimple}
                  />
                )}
              {documentosAsociados && documentosAsociados.length > 0 && (
                <ModalVerDocumentosDocumetosAsosiados
                  documentosAsociados={documentosAsociados}
                  descargarDocumentoAsociado={descargarDocumentoAsociado}
                />
              )}
              {documentSimple?.tipo === "factura" ? (
                <ModalVerPagosYCobros
                  data={flujosTesoreriaFactura}
                  setShowAddPagoModal={setShowAddPagoModal}
                  loading={flujoTesoreriaFacturaIsLoading}
                  importePendiente={documentSimple.data?.importePendiente}
                  documentData={documentSimple.data}
                />
              ) : null}

              {documentSimple?.tipo === "factura" ? (
                <ModalEstados
                  estadoRevisionActual={
                    documentSimple.data.estadoRevisionActual
                  }
                  tipoFlujoTesoreria={documentSimple?.data?.tipoFlujoTesoreria}
                  estadoCobro={documentSimple?.data?.estadoCobro?.estadoCobro}
                  estadoPago={documentSimple?.data?.estadoPago?.estadoPago}
                  iconColor='#004AD1'
                  isFactura={Boolean(docType === 2)}
                />
              ) : null}
            </>
          )}
          <div className='tw-flex tw-flex-row tw-w-fit tw-bg-neutral-10 tw-rounded tw-p-1 tw-my-2 tw-gap-1'>
            <div
              className={`tw-flex tw-items-center tw-px-6 tw-py-2 tw-rounded  ${
                tabSelected === 1
                  ? "tw-bg-white tw-font-medium"
                  : "tw-bg-neutral-10 tw-cursor-pointer"
              }`}
              onClick={() => setTabSelected(1)}
            >
              {t("crear.presupuesto.modal.docLabel")}
            </div>
            {!isVenta && (
              <div
                className={`tw-flex tw-items-center tw-px-6 tw-py-2 tw-rounded ${
                  tabSelected === 2
                    ? "tw-bg-white tw-font-medium"
                    : "tw-bg-neutral-10 tw-cursor-pointer"
                }`}
                onClick={() => setTabSelected(2)}
              >
                {t("crear.presupuesto.modal.flujoLabel")}
              </div>
            )}
          </div>

          <div>
            {tabSelected === TAB_OPTIONS(t)[1].value ? (
              <ModalVerDocumentoArchivosAdjuntos
                archivosAdjuntos={archivosAdjuntos}
                agregarArchivoAdjunto={agregarArchivoAdjunto}
                eliminarArchivoAdjunto={eliminarArchivoAdjunto}
                descargarArchivoAdjunto={descargarArchivoAdjunto}
                isVentaBorrador={facturaVentaBorrador}
              />
            ) : (
              <DocumentDetailCard validacion={documentoPendienteValidar} />
            )}
          </div>
        </div>

        <div className='col-7'>
          {pdfDocumentIsLoading ? (
            <Skeleton className='tw-w-full !tw-h-full' />
          ) : (
            <NativePDF
              pdf={pdfDocument}
              type='stream'
            />
          )}
        </div>
      </div>
      {documentSimple?.tipo === "factura" ? (
        <Modal
          header={`${t("crear.factura.addPagoCobro.add")} ${
            documentSimple?.data?.tipoFlujoTesoreria ===
            TIPO_FLUJO_TESORERIA.COBRO
              ? t("crear.factura.addPagoCobro.titleCobroSing")
              : t("crear.factura.addPagoCobro.titlePagoSing")
          }`}
          visible={showAddPagoModal.show}
          onHide={() =>
            setShowAddPagoModal({
              data: null,
              show: false,
            })
          }
          size='extra-big'
          maskClassName='!tw-z-30'
        >
          <AddPagoCobroModal
            mode={documentSimple.data?.tipoFlujoTesoreria}
            handleOpenCreateNewPago={handleOpenCreateNewPago}
            facturaId={documentoID}
            document={documentSimple.data}
            onHide={() => {
              setShowAddPagoModal({
                data: null,
                show: false,
              })
              setNewPagoCobro([])
            }}
            refetchCobroPago={() => {
              flujoTesoreriaFacturaRefetch()
              refetchDocumentSimple()
              refreshData()
            }}
            newPagoCobro={newPagoCobro}
            setNewPagoCobro={setNewPagoCobro}
          />
        </Modal>
      ) : null}
      {documentSimple?.tipo === "factura" ? (
        <Modal
          header={`${t("pagoCobro.modal.headerNew")} ${
            documentSimple?.data?.tipoFlujoTesoreria ===
            TIPO_FLUJO_TESORERIA.PAGO
              ? t("pagoCobro.modal.labelPayment")
              : t("pagoCobro.modal.labelCollection")
          }`}
          visible={showCreateModal}
          onHide={() => handleOpenCreateModal(false)}
          size='default'
          className='departaments-modal'
          maskClassName='!tw-z-40'
        >
          <NewPagoForm
            // @ts-expect-error TS(2322): Type '{ hideModal: () => void; isEditView: boolean...
            hideModal={() => handleOpenCreateModal(false)}
            isEditView={false}
            onHide={() => handleOpenCreateModal(false)}
            handleCreate={handleCreate}
            data={{
              empresaId: documentSimple?.data?.empresa?.id,
              razonSocial: documentSimple?.data?.empresa?.razonSocial,
              comeFromFactura: true,
              facturaId: documentSimple?.data?.id,
              tipo: documentSimple?.data?.tipoFlujoTesoreria,
              importe: documentSimple?.data?.importePendiente
                ? documentSimple?.data?.importePendiente
                : 0,
              remesaId: null,
            }}
            t={t}
          />
        </Modal>
      ) : null}
      <PagoCobroManualForm
        show={showModalPagoCobroManual.show}
        isVenta={isVenta}
        onHide={() => {
          setShowModalPagoCobroManual({
            show: false,
            id: undefined,
            serie: undefined,
          })
        }}
        document={{
          serie: showModalPagoCobroManual.serie,
          id: showModalPagoCobroManual.id,
        }}
        updateData={() => {
          refetchDocumentSimple()
          refreshData()
        }}
        t={t}
      />
    </Dialog>
  )
}

export default ModalVerDocumento
